import { type BioContext } from '@ps/functions/bio/bioContext';
import { bioUrl } from '@ps/functions/bio/bioUrls';

/**
 * A shareable bio entity, described by contextual FACTS (ids / slugs) — never a
 * pre-built path. {@link shareUrlForTarget} turns it into the canonical share URL
 * for the page's {@link BioContext}, so every share link is custom-domain aware
 * (absolute, on the host it was shared from) and prefix-correct in ONE place.
 *
 * Each variant is its own named interface (per the discriminated-union rules);
 * add a kind here + a branch in {@link shareUrlForTarget} to make a new surface
 * shareable through {@link ShareButton}.
 */
export interface ShareHomeTarget {
  kind: 'home';
}

export interface ShareProductTarget {
  kind: 'product';
  id: string;
  slug?: string | null;
}

export interface ShareCollectionTarget {
  kind: 'collection';
  id: string;
}

export interface ShareSeriesTarget {
  kind: 'series';
  slug: string;
}

export interface ShareCourseTarget {
  kind: 'course';
  slug: string;
  /** A chapter under the course; omit to share the course itself. */
  chapterSlug?: string;
}

/**
 * A single clip, shared via its owner's deep-link. A clip always lives under a
 * series or course here (there's no standalone `/clip/<id>` route), so the share
 * URL is the owner's detail path + `/videos/<clipId>` — the same permalink a
 * cold deep-link / refresh resolves to.
 */
export interface ShareClipTarget {
  kind: 'clip';
  /** The owning series / course slug. */
  slug: string;
  /** The clip's video id (`popClip.metaIdentifier`). */
  clipId: string;
  /** True when the owner is a course (`/course/<slug>/…`) vs a series. */
  isCourse?: boolean;
}

export interface ShareVideoTarget {
  kind: 'video';
  clipId: string;
}

export interface ShareLiveTarget {
  kind: 'live';
  showId: string;
}

export interface ShareLivePreviewTarget {
  kind: 'live-preview';
  showId: string;
}

export interface ShareCommunityChannelTarget {
  kind: 'community-channel';
  slug: string;
}

export type ShareTarget =
  | ShareHomeTarget
  | ShareProductTarget
  | ShareCollectionTarget
  | ShareSeriesTarget
  | ShareCourseTarget
  | ShareClipTarget
  | ShareVideoTarget
  | ShareLiveTarget
  | ShareLivePreviewTarget
  | ShareCommunityChannelTarget;

/**
 * The canonical, absolute share URL for a {@link ShareTarget} on the page's
 * {@link BioContext}. Builds via `bioUrl(ctx, { share: true })` — so the link is
 * prefix-free (never the `~` soft-nav sentinel), absolute, and rooted at the
 * current host (the creator's custom domain when the visitor is on it).
 *
 * The absolute origin comes from the {@link BioContext} (`domain` → the creator's
 * custom domain, else the default host), so this is deterministic + SSR-safe — no
 * reliance on `window.location.origin`.
 */
export function shareUrlForTarget(
  bioContext: BioContext,
  target: ShareTarget,
): string {
  const builder = bioUrl(bioContext, { share: true });
  switch (target.kind) {
    case 'home':
      return builder.home();
    case 'product':
      return builder.product({ id: target.id, slug: target.slug });
    case 'collection':
      return builder.collection(target.id);
    case 'series':
      return builder.series(target.slug);
    case 'course': {
      const { slug, chapterSlug } = target;
      return chapterSlug
        ? builder.course(slug, course => course.chapter(chapterSlug))
        : builder.course(slug);
    }
    case 'clip': {
      const { slug, clipId, isCourse } = target;
      return isCourse
        ? builder.course(slug, course => course.video(clipId))
        : builder.series(slug, video => video.video(clipId));
    }
    case 'video':
      return builder.video(target.clipId);
    case 'live':
      return builder.live(target.showId);
    case 'live-preview':
      return builder.livePreview(target.showId);
    case 'community-channel':
      return builder.communityChannels(target.slug);
    default: {
      const exhaustive: never = target;
      throw new Error(
        `shareUrlForTarget: unhandled share target ${JSON.stringify(exhaustive)}`,
      );
    }
  }
}
